import React, { useRef, useState, useEffect } from 'react';
import { Booking } from '../../types/booking';
import { bookingService } from '../../services/bookingService';
import { apiService } from '../../services/api';

interface BookingInvoiceProps {
  booking: Booking;
  onClose?: () => void;
  onSave?: () => void; // Callback to refresh booking list after save
}

interface EditableInvoiceData {
  // Customer fields
  customerName: string;
  customerAddress: string;
  customerPhone: string;
  customerZipCode: string;
  customerLicense: string;
  customerLicenseExpiry: string;
  customerDOB: string;
  customerEmail: string;
  
  // Pickup/Dropoff
  pickupDate: string;
  pickupLocation: string;
  dropoffDate: string;
  dropoffLocation: string;
  totalDays: string;
  
  // Vehicle fields
  vehicleNumber: string;
  vehicleMakeModel: string;
  vehicleCondition: string;
  fuelLevelOut: string;
  fuelLevelIn: string;
  odometerOut: string;
  odometerIn: string;
  dailyFreeKm: string;
  
  // Charges
  additionalKmsCharge: string;
  additionalKmsDescription: string;
  securityDeposit: string;
  totalGST: string;
  specialDiscount: string;
  totalAmount: string;
  specialComments: string;
  bookingReference: string;
}

type RateTier = {
  minDays: number;
  maxDays?: number | null;
  rate: number;
};

// Editable field component - moved outside to prevent re-creation
const EditableField: React.FC<{
  value: string;
  onChange: (value: string) => void;
  className?: string;
  style?: React.CSSProperties;
  multiline?: boolean;
  rows?: number;
  type?: 'text' | 'date';
}> = React.memo(({ value, onChange, className = '', style = {}, multiline = false, type = 'text', rows = 2 }) => {
  if (multiline) {
    return (
      <textarea
        value={value}
        onChange={(e) => onChange(e.target.value)}
        className={`${className} border border-gray-300 bg-yellow-50 px-1 py-0.5 text-xs focus:outline-none focus:ring-1 focus:ring-blue-400 focus:border-blue-400 print:border-0 print:bg-transparent print:px-0`}
        style={{ 
          minWidth: '100px',
          width: '100%',
          minHeight: `${rows * 16}px`, 
          resize: 'none', 
          fontSize: '13px', 
          lineHeight: '1.1',
          whiteSpace: 'pre-wrap',
          wordBreak: 'break-word',
          ...style
        }}
        rows={rows}
      />
    );
  }

  const defaultWidth = style.width || '150px';
  
  return (
    <input
      type={type}
      value={value}
      onChange={(e) => onChange(e.target.value)}
      className={`${className} border border-gray-300 bg-yellow-50 px-1 py-0.5 text-xs focus:outline-none focus:ring-1 focus:ring-blue-400 focus:border-blue-400 print:border-0 print:bg-transparent print:px-0`}
      style={{ 
        minWidth: typeof defaultWidth === 'string' ? defaultWidth : `${defaultWidth}px`,
        width: `${Math.max(value.length * 8 + 20, typeof defaultWidth === 'number' ? defaultWidth : parseInt(defaultWidth) || 150)}px`,
        fontSize: '13px', 
        lineHeight: '1.1', 
        height: '22px',
        ...style
      }}
      size={value.length || 10}
    />
  );
});

EditableField.displayName = 'EditableField';

const BookingInvoice: React.FC<BookingInvoiceProps> = ({ booking, onClose, onSave }) => {
  const invoiceRef = useRef<HTMLDivElement>(null);
  const [isSaving, setIsSaving] = useState(false);
  const [saveMessage, setSaveMessage] = useState<{ type: 'success' | 'error', text: string } | null>(null);
  const [printedAt, setPrintedAt] = useState('');

  const formatDate = (date: Date | string) => {
    return new Date(date).toLocaleDateString('en-GB', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric'
    });
  };

  const formatDateForInput = (date: Date | string) => {
    if (!date) return '';
    const d = new Date(date);
    const year = d.getFullYear();
    const month = String(d.getMonth() + 1).padStart(2, '0');
    const day = String(d.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  };

  const [editableData, setEditableData] = useState<EditableInvoiceData>({
    customerName: '',
    customerAddress: '',
    customerPhone: '',
    customerZipCode: '',
    customerLicense: '',
    customerLicenseExpiry: '',
    customerDOB: '',
    customerEmail: '',
    pickupDate: '',
    pickupLocation: '',
    dropoffDate: '',
    dropoffLocation: '',
    totalDays: '',
    vehicleNumber: '',
    vehicleMakeModel: '',
    vehicleCondition: 'Clean',
    fuelLevelOut: '',
    fuelLevelIn: '',
    odometerOut: '',
    odometerIn: '',
    dailyFreeKm: '100',
    additionalKmsCharge: '',
    additionalKmsDescription: '$Cost for KM Over Use allowable free KM',
    securityDeposit: '0.00',
    totalGST: '',
    specialDiscount: '',
    totalAmount: '',
    specialComments: 'Digital receipt emailed',
    bookingReference: '',
  });

  // Initialize editable data from booking - only once
  useEffect(() => {
    const customer = booking.customer;
    const car = booking.car;
    // Calculate GST (10% of total)
    const totalAmount = booking.totalAmount || 0;
    const gstAmount = totalAmount - (totalAmount / 1.1);
    
    setEditableData({
      customerName: customer ? `${customer.firstName || ''} ${customer.lastName || ''}`.trim() : '',
      customerAddress: customer?.address
        ? typeof customer.address === 'string'
          ? customer.address
          : `${customer.address.street || ''}, ${customer.address.city || ''}, ${customer.address.state || ''} ${customer.address.zipCode || ''}`.trim()
        : '',
      customerPhone: customer?.contact || customer?.phone || '',
      customerZipCode: typeof customer?.address === 'object' ? customer.address.zipCode || '' : '',
      customerLicense: customer?.licenseNumber || '',
      customerLicenseExpiry: customer?.licenseExpiryDate ? formatDateForInput(customer.licenseExpiryDate) : '',
      customerDOB: customer?.dateOfBirth ? formatDateForInput(customer.dateOfBirth) : '',
      customerEmail: customer?.email || '',
      pickupDate: booking.pickUpDate ? formatDate(booking.pickUpDate) : '',
      pickupLocation: booking.pickUpLocation || '',
      dropoffDate: booking.returnDate ? formatDate(booking.returnDate) : '',
      dropoffLocation: booking.returnLocation || '',
      totalDays: calculateDays().toFixed(2),
      vehicleNumber: car?.licensePlate || car?.registrationNumber || '',
      vehicleMakeModel: car ? `${car.brand || ''} ${car.model || ''} ${car.name || ''}`.trim() : '',
      vehicleCondition: 'Clean',
      fuelLevelOut: booking.fuelLevelStart || '',
      fuelLevelIn: booking.fuelLevelEnd || '',
      odometerOut: typeof booking.startMileage === 'number' ? booking.startMileage.toFixed(2) : '',
      odometerIn: typeof booking.endMileage === 'number' ? booking.endMileage.toFixed(2) : '',
      dailyFreeKm: '100.00',
      additionalKmsCharge: typeof booking.additionalCharges === 'number' ? booking.additionalCharges.toFixed(2) : '0.00',
      additionalKmsDescription: booking.additionalChargesDescription || '$Cost for KM Over Use allowable free KM',
      securityDeposit: '0.00',
      totalGST: gstAmount.toFixed(2),
      specialDiscount: (booking.discountAmount || 0).toFixed(2),
      totalAmount: totalAmount.toFixed(2),
      specialComments: booking.specialRequests || booking.customerNotes || 'Digital receipt emailed',
      bookingReference: booking.bookingReference || '',
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handlePrint = () => {
    // Ensure the timestamp is rendered before the print dialog snapshots the page
    setPrintedAt(formatPrintedAt());
    setTimeout(() => {
      window.print();
    }, 0);
  };

  const formatPrintedAt = () => {
    const now = new Date();
    return now.toLocaleString('en-GB', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  useEffect(() => {
    const handleBeforePrint = () => {
      setPrintedAt(formatPrintedAt());
    };
    window.addEventListener('beforeprint', handleBeforePrint);
    return () => window.removeEventListener('beforeprint', handleBeforePrint);
  }, []);

  const handleFieldChange = (field: keyof EditableInvoiceData, value: string) => {
    setEditableData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleSaveChanges = async () => {
    setIsSaving(true);
    setSaveMessage(null);

    try {
      // Parse dates from DD/MM/YYYY format to ISO format
      const parseDate = (dateStr: string) => {
        if (!dateStr) return undefined;
        const parts = dateStr.split('/');
        if (parts.length === 3) {
          // DD/MM/YYYY format
          return `${parts[2]}-${parts[1]}-${parts[0]}`;
        }
        return dateStr; // Already in correct format
      };

      // Prepare booking update data - include all editable fields
      const bookingUpdateData: any = {
        pickUpDate: parseDate(editableData.pickupDate),
        pickUpLocation: editableData.pickupLocation,
        returnDate: parseDate(editableData.dropoffDate),
        returnLocation: editableData.dropoffLocation,
        startMileage: parseFloat(editableData.odometerOut) || undefined,
        endMileage: parseFloat(editableData.odometerIn) || undefined,
        fuelLevelStart: editableData.fuelLevelOut || undefined,
        fuelLevelEnd: editableData.fuelLevelIn || undefined,
        additionalCharges: parseFloat(editableData.additionalKmsCharge) || undefined,
        additionalChargesDescription: editableData.additionalKmsDescription || undefined,
        totalAmount: parseFloat(editableData.totalAmount) || undefined,
        taxAmount: parseFloat(editableData.totalGST) || undefined,
        discountAmount: parseFloat(editableData.specialDiscount) || undefined,
        specialRequests: editableData.specialComments || undefined,
      };

      // Update booking
      console.log('📝 Saving booking changes:', bookingUpdateData);
      const bookingResponse = await bookingService.updateBooking(booking._id, bookingUpdateData);
      
      if (!bookingResponse.success) {
        throw new Error(bookingResponse.error?.message || 'Failed to update booking');
      }
      
      console.log('✅ Booking updated successfully:', bookingResponse.data);

      // Update car's license plate if changed - use admin endpoint
      const car = booking.car;
      if (car && typeof car === 'object' && car._id) {
        const originalPlate = car.licensePlate || car.registrationNumber || '';
        if (editableData.vehicleNumber && editableData.vehicleNumber !== originalPlate) {
          console.log('📝 Updating vehicle license plate:', editableData.vehicleNumber);
          const carUpdateData = {
            licensePlate: editableData.vehicleNumber,
            registrationNumber: editableData.vehicleNumber,
          };
          // Use admin endpoint which supports licensePlate field
          const carResponse = await apiService.put(`/api/cars/admin/${car._id}`, carUpdateData);
          
          if (!carResponse.success) {
            console.warn('⚠️ Vehicle update failed:', carResponse.error?.message);
          } else {
            console.log('✅ Vehicle license plate updated successfully');
          }
        }
      }

      // Prepare customer update data
      const nameParts = editableData.customerName.trim().split(' ');
      const firstName = nameParts[0] || '';
      const lastName = nameParts.slice(1).join(' ') || '';

      const customerUpdateData: any = {
        firstName,
        lastName,
        email: editableData.customerEmail,
        contact: editableData.customerPhone,
        licenseNumber: editableData.customerLicense,
        address: editableData.customerAddress,
      };

      // Add optional date fields if they have values
      if (editableData.customerDOB) {
        customerUpdateData.dateOfBirth = editableData.customerDOB;
      }
      if (editableData.customerLicenseExpiry) {
        customerUpdateData.licenseExpiryDate = editableData.customerLicenseExpiry;
      }
      if (editableData.customerZipCode) {
        customerUpdateData.zipCode = editableData.customerZipCode;
      }

      // Update customer if we have a customer ID
      if (booking.customer && typeof booking.customer === 'object' && booking.customer._id) {
        console.log('📝 Saving customer changes:', customerUpdateData);
        const customerResponse = await apiService.patch(`/api/customers/${booking.customer._id}`, customerUpdateData);
        
        if (!customerResponse.success) {
          console.warn('⚠️ Customer update failed, but booking was saved');
        } else {
          console.log('✅ Customer updated successfully');
        }
      }

      setSaveMessage({ type: 'success', text: 'Changes saved successfully!' });
      
      // Call onSave callback to refresh the booking list immediately
      if (onSave) {
        onSave();
      }

    } catch (error: any) {
      console.error('Error saving changes:', error);
      setSaveMessage({ 
        type: 'error', 
        text: error.response?.data?.message || 'Failed to save changes. Please try again.' 
      });
    } finally {
      setIsSaving(false);
      // Clear message after 5 seconds
      setTimeout(() => setSaveMessage(null), 5000);
    }
  };

  const calculateDays = () => {
    const pickUp = new Date(booking.pickUpDate);
    const returnDate = new Date(booking.returnDate);
    const diffTime = Math.abs(returnDate.getTime() - pickUp.getTime());
    return Math.ceil(diffTime / (1000 * 60 * 60 * 24));
  };

  const customer = booking.customer;
  const car = booking.car;
  const days = calculateDays();

  // Calculate total kilometers from editable data
  const totalKmsDrivenCalc = () => {
    const odometerOutValue = editableData.odometerOut?.trim();
    const odometerInValue = editableData.odometerIn?.trim();

    // Keep it at 0 until both values are entered.
    if (!odometerOutValue || !odometerInValue) {
      return 0;
    }

    const odometerOut = Number(odometerOutValue);
    const odometerIn = Number(odometerInValue);

    if (!Number.isFinite(odometerOut) || !Number.isFinite(odometerIn)) {
      return 0;
    }

    // Prevent negative display from partial/incorrect input.
    return Math.max(0, odometerIn - odometerOut);
  };

  const tiers: RateTier[] = Array.isArray(car?.rates) ? (car!.rates as RateTier[]) : [];

  // Generic helper to find a rate tier
  const findRate = (min: number, max?: number | null) => {
    if (!tiers.length) return 0;
    const match = tiers.find(t =>
      t.minDays === min &&
      (
        (max === undefined || max === null)
          ? (t.maxDays === undefined || t.maxDays === null)
          : t.maxDays === max
      )
    );
    return match?.rate ?? 0;
  };

  // Individual getters used by the Charges table
  const get1DayRate = () => findRate(1, 1);
  const get2DayRate = () => findRate(2, 2);
  const get3To6DayRate = () => findRate(3, 6);
  const get7PlusDayRate = () => findRate(7, null);

  // Determine which pricing tier applies based on booking duration
  const getApplicableRate = () => {
    if (!tiers.length) return { label: 'Daily Rate $:', rate: 0 };

    if (days === 1) {
      return { label: '1-Day Rate $:', rate: get1DayRate() };
    } else if (days === 2) {
      return { label: '2-Days Daily $:', rate: get2DayRate() };
    } else if (days >= 3 && days <= 6) {
      return { label: '3-6 Days Daily $:', rate: get3To6DayRate() };
    } else if (days >= 7) {
      return { label: '7+ Days Daily $:', rate: get7PlusDayRate() };
    }

    return { label: 'Daily Rate $:', rate: 0 };
  };

  const applicableRate = getApplicableRate();

  const invoiceExtras: any[] =
    Array.isArray((booking as any).extras) &&
    (booking as any).extras.some((e: any) => e && typeof e === 'object')
      ? (booking as any).extras
      : Array.isArray((booking as any).extrasData)
      ? (booking as any).extrasData
      : [];

  // Get extras amounts - check both name and title fields
  const damageWaiverExtra = invoiceExtras.find((e: any) =>
    (e?.name?.toLowerCase().includes('damage') || e?.name?.toLowerCase().includes('waiver')) ||
    (e?.title?.toLowerCase().includes('damage') || e?.title?.toLowerCase().includes('waiver'))
  );
  const babySeatExtra = invoiceExtras.find((e: any) =>
    (e?.name?.toLowerCase().includes('baby') || e?.name?.toLowerCase().includes('seat') || e?.name?.toLowerCase().includes('child')) ||
    (e?.title?.toLowerCase().includes('baby') || e?.title?.toLowerCase().includes('seat') || e?.title?.toLowerCase().includes('child'))
  );
  const airportPickupExtra = invoiceExtras.find((e: any) =>
    ((e?.name?.toLowerCase().includes('airport') || e?.name?.toLowerCase().includes('pickup')) && e?.name?.toLowerCase().includes('pickup')) ||
    ((e?.title?.toLowerCase().includes('airport') || e?.title?.toLowerCase().includes('pickup')) && e?.title?.toLowerCase().includes('pickup'))
  );
  const airportDropoffExtra = invoiceExtras.find((e: any) =>
    ((e?.name?.toLowerCase().includes('airport') || e?.name?.toLowerCase().includes('drop')) && (e?.name?.toLowerCase().includes('drop') || e?.name?.toLowerCase().includes('dropoff'))) ||
    ((e?.title?.toLowerCase().includes('airport') || e?.title?.toLowerCase().includes('drop')) && (e?.title?.toLowerCase().includes('drop') || e?.title?.toLowerCase().includes('dropoff')))
  );

  const extendedAreaExtra = invoiceExtras.find((e: any) =>
    (e?.name?.toLowerCase().includes('extended') && e?.name?.toLowerCase().includes('area')) ||
    (e?.title?.toLowerCase().includes('extended') && e?.title?.toLowerCase().includes('area')) ||
    (e?.name?.toLowerCase().includes('area') && e?.name?.toLowerCase().includes('use')) ||
    (e?.title?.toLowerCase().includes('area') && e?.title?.toLowerCase().includes('use'))
  );

  const damageWaiverAmount = Number(damageWaiverExtra?.price) || 0;
  const babySeatAmount = Number(babySeatExtra?.price) || 0;
  const airportPickupAmount = Number(airportPickupExtra?.price) || 0;
  const airportDropoffAmount = Number(airportDropoffExtra?.price) || 0;
  const extendedAreaAmount = Number(extendedAreaExtra?.price) || 0;

  // Calculate free kilometers for the trip
  const dailyFreeKm = 100;
  const totalFreeKm = days * dailyFreeKm;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4 print:p-0 print:bg-white">
      <div className="bg-white rounded-lg shadow-xl max-w-5xl w-full max-h-[95vh] overflow-y-auto print:max-h-none print:shadow-none print:rounded-none">
        {/* Action Buttons - Hidden in print */}
        <div className="sticky top-0 bg-white border-b border-gray-200 px-6 py-4 flex justify-between items-center print:hidden z-10">
          <div>
            <h2 className="text-xl font-semibold text-gray-900">Rental Agreement / Invoice (Editable)</h2>
            {saveMessage && (
              <p className={`text-sm mt-1 ${saveMessage.type === 'success' ? 'text-green-600' : 'text-red-600'}`}>
                {saveMessage.text}
              </p>
            )}
          </div>
          <div className="flex space-x-3">
            <button
              onClick={handleSaveChanges}
              disabled={isSaving}
              className={`px-4 py-2 rounded-md flex items-center ${
                isSaving 
                  ? 'bg-gray-400 cursor-not-allowed' 
                  : 'bg-green-600 hover:bg-green-700'
              } text-white`}
            >
              {isSaving ? (
                <>
                  <svg className="animate-spin h-5 w-5 mr-2" fill="none" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                  </svg>
                  Saving...
                </>
              ) : (
                <>
                  <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7H5a2 2 0 00-2 2v9a2 2 0 002 2h14a2 2 0 002-2V9a2 2 0 00-2-2h-3m-1 4l-3 3m0 0l-3-3m3 3V4" />
                  </svg>
                  Save Changes
                </>
              )}
            </button>
            <button
              onClick={handlePrint}
              className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 flex items-center"
            >
              <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 17h2a2 2 0 002-2v-4a2 2 0 00-2-2H5a2 2 0 00-2 2v4a2 2 0 002 2h2m2 4h6a2 2 0 002-2v-4a2 2 0 00-2-2H9a2 2 0 00-2 2v4a2 2 0 002 2zm8-12V5a2 2 0 00-2-2H9a2 2 0 00-2 2v4h10z" />
              </svg>
              Print
            </button>
            {onClose && (
              <button
                onClick={onClose}
                className="px-4 py-2 bg-gray-200 text-gray-700 rounded-md hover:bg-gray-300"
              >
                Close
              </button>
            )}
          </div>
        </div>

        {/* Invoice Content */}
        <div
          ref={invoiceRef}
          id="invoice-content"
          className="bg-white"
          style={{ fontFamily: 'Courier New, monospace', fontSize: '12px', lineHeight: '1.0', padding: '8px' }}
        >
          {/* Header with Logo and Company Info */}
          <div className="flex justify-between items-start border-b-2 border-black">
            <div className="flex items-start">
              <div className="text-red-600 font-bold text-3xl mr-4" style={{ fontFamily: 'Arial, sans-serif' }}>
                TAK8
              </div>
              <div>
                <div className="font-bold">TAK8 Car Rental</div>
                <div>17 Regal Place East Perth</div>
                <div>Phone:+61893415334</div>
                <div>Mobile:+61409521954</div>
              </div>
            </div>
            <div>
              <div className="font-bold">TAK8 Car Rental</div>
              <div>Commonwealth Bank</div>
              <div>BSB: 062 692</div>
              <div>ACC: 72273629</div>
            </div>
            <div className="text-right">
              <div className="font-bold">CONTRACT NUMBER #</div>
              <div className="font-bold text-right">{editableData.bookingReference || 'N/A'}</div>
              <div className="text-blue-600 underline">www.tak8.com.au</div>
              <div className="font-bold">TAK8 Car Rental</div>
              <div>ABN:30660952643</div>
            </div>
          </div>

          {/* Customer Information */}
          <div className="border-r border-black">
            <div className="font-bold text-center" style={{ borderTop: '3px double #000', borderBottom: '3px double #000', padding: '0' }}>Customer Information</div>
            <table className="w-full border-collapse" style={{ fontSize: '13px', tableLayout: 'fixed' }}>
              <tbody>
                <tr>
                  <td className="px-1 py-0.5 border-r border-black" style={{ width: '255px' }}>Name:</td>
                  <td className="px-1 py-0.5 border-r border-black">
                    <EditableField
                      value={editableData.customerName}
                      onChange={(val) => handleFieldChange('customerName', val)}
                      className="font-bold"
                    />
                  </td>
                </tr>
                <tr>
                  <td className="px-1 py-0.5 border-r border-black">Address:</td>
                  <td className="px-1 py-0.5 border-r border-black">
                    <EditableField
                      value={editableData.customerAddress}
                      onChange={(val) => handleFieldChange('customerAddress', val)}
                    />
                  </td>
                </tr>
                <tr>
                  <td className="px-1 py-0.5 border-r border-black">Phone :</td>
                  <td className="px-1 py-0.5 border-r border-black">
                    <EditableField
                      value={editableData.customerPhone}
                      onChange={(val) => handleFieldChange('customerPhone', val)}
                      style={{ width: '150px', display: 'inline-block' }}
                    />
                    <span style={{ marginLeft: '40px' }}>
                      <EditableField
                        value={editableData.customerZipCode}
                        onChange={(val) => handleFieldChange('customerZipCode', val)}
                        style={{ width: '80px', display: 'inline-block' }}
                      />
                    </span>
                  </td>
                </tr>
                <tr>
                  <td className="px-1 py-0.5 border-r border-black">Driver License:</td>
                  <td className="px-1 py-0.5 border-r border-black">
                    <EditableField
                      value={editableData.customerLicense}
                      onChange={(val) => handleFieldChange('customerLicense', val)}
                      style={{ width: '200px', display: 'inline-block' }}
                    />
                  </td>
                </tr>
                <tr>
                  <td className="px-1 py-0.5 border-r border-black">License Expiry Date:</td>
                  <td className="px-1 py-0.5 border-r border-black">
                    <EditableField
                      value={editableData.customerLicenseExpiry}
                      onChange={(val) => handleFieldChange('customerLicenseExpiry', val)}
                      type="date"
                      style={{ width: '140px', display: 'inline-block' }}
                    />
                    <span style={{ marginLeft: '35px' }}>DOB:</span>
                    <span style={{ marginLeft: '12px' }}>
                      <EditableField
                        value={editableData.customerDOB}
                        onChange={(val) => handleFieldChange('customerDOB', val)}
                        type="date"
                        style={{ width: '140px', display: 'inline-block' }}
                      />
                    </span>
                  </td>
                </tr>
                <tr>
                  <td className="px-1 py-0.5 border-r border-black">Second ID:</td>
                  <td className="px-1 py-0.5 border-r border-black">Yes</td>
                </tr>
                <tr>
                  <td className="px-1 py-0.5 border-r border-black">Email</td>
                  <td className="px-2 text-blue-600 border-r border-black">
                    <EditableField
                      value={editableData.customerEmail}
                      onChange={(val) => handleFieldChange('customerEmail', val)}
                    />
                  </td>
                </tr>
              </tbody>
            </table>
          </div>

          {/* Pick Up / Drop Off Location Information */}
          <div>
            <div className="font-bold text-center" style={{ borderTop: '3px double #000', borderBottom: '3px double #000', padding: '0' }}>Pick Up / Drop Off Location/Information</div>
            <table className="w-full border-collapse" style={{ fontSize: '13px', tableLayout: 'fixed' }}>
              <tbody>
                <tr>
                  <td className="px-1 py-0.5 border-r border-black" style={{ width: '255px' }}>Vehicle Pickup:</td>
                  <td className="px-1" style={{ width: '120px' }}>
                    <EditableField
                      value={editableData.pickupDate}
                      onChange={(val) => handleFieldChange('pickupDate', val)}
                    />
                  </td>
                  <td className="px-1 py-0.5 border-r border-black">
                    <EditableField
                      value={editableData.pickupLocation}
                      onChange={(val) => handleFieldChange('pickupLocation', val)}
                    />
                  </td>
                </tr>
                <tr>
                  <td className="px-1 py-0.5 border-r border-black">Vehicle Drop Off:</td>
                  <td className="px-1">
                    <EditableField
                      value={editableData.dropoffDate}
                      onChange={(val) => handleFieldChange('dropoffDate', val)}
                    />
                  </td>
                  <td className="px-1 py-0.5 border-r border-black">
                    <EditableField
                      value={editableData.dropoffLocation}
                      onChange={(val) => handleFieldChange('dropoffLocation', val)}
                    />
                  </td>
                </tr>
                <tr>
                  <td className="px-1 py-0.5 border-r border-black">Total Days</td>
                  <td className="px-1">
                    <EditableField
                      value={editableData.totalDays}
                      onChange={(val) => handleFieldChange('totalDays', val)}
                      style={{ width: '60px' }}
                    />
                  </td>
                  <td className="px-1 py-0.5 border-r border-black"></td>
                </tr>
              </tbody>
            </table>
          </div>

          {/* Vehicle Information */}
          <div>
            <div className="font-bold text-center" style={{ borderTop: '3px double #000', borderBottom: '3px double #000', padding: '0' }}>Vehicle Information</div>
            <table className="w-full border-collapse" style={{ fontSize: '13px', tableLayout: 'fixed' }}>
              <tbody>
                <tr>
                  <td className="px-1 py-0.5 border-r border-black" style={{ width: '255px' }}>Vehicle#</td>
                  <td className="px-1 py-0.5 border-r border-black">
                    <EditableField
                      value={editableData.vehicleNumber}
                      onChange={(val) => handleFieldChange('vehicleNumber', val)}
                    />
                  </td>
                </tr>
                <tr>
                  <td className="px-1 py-0.5 border-r border-black">Make/Description:</td>
                  <td className="px-1 py-0.5 border-r border-black">
                    <EditableField
                      value={editableData.vehicleMakeModel}
                      onChange={(val) => handleFieldChange('vehicleMakeModel', val)}
                    />
                  </td>
                </tr>
                <tr>
                  <td className="px-1 py-0.5 border-r border-black">Fuel level Out / In</td>
                  <td className="px-1 py-0.5 border-r border-black">
                    <EditableField
                      value={editableData.fuelLevelOut}
                      onChange={(val) => handleFieldChange('fuelLevelOut', val)}
                      style={{ width: '60px', display: 'inline-block' }}
                    />
                    {' / '}
                    <EditableField
                      value={editableData.fuelLevelIn}
                      onChange={(val) => handleFieldChange('fuelLevelIn', val)}
                      style={{ width: '60px', display: 'inline-block' }}
                    />
                  </td>
                </tr>
                <tr>
                  <td className="px-1 py-0.5 border-r border-black">Vehicle Condition:</td>
                  <td className="px-1 py-0.5 border-r border-black">
                    <EditableField
                      value={editableData.vehicleCondition}
                      onChange={(val) => handleFieldChange('vehicleCondition', val)}
                    />
                  </td>
                </tr>
                <tr>
                  <td className="px-1 py-0.5 border-r border-black">Odo meter Out:</td>
                  <td className="px-1 py-0.5 border-r border-black">
                    <EditableField
                      value={editableData.odometerOut}
                      onChange={(val) => handleFieldChange('odometerOut', val)}
                    />
                  </td>
                </tr>
                <tr>
                  <td className="px-1 py-0.5 border-r border-black">Odo meter In:</td>
                  <td className="px-1 py-0.5 border-r border-black">
                    <EditableField
                      value={editableData.odometerIn}
                      onChange={(val) => handleFieldChange('odometerIn', val)}
                    />
                  </td>
                </tr>
                <tr>
                  <td className="px-1 py-0.5 border-r border-black">Total Kms Driven:</td>
                  <td className="px-1 py-0.5 border-r border-black">{totalKmsDrivenCalc().toFixed(2)}</td>
                </tr>
                <tr>
                  <td className="px-1 py-0.5 border-r border-black">Daily free Km:</td>
                  <td className="px-1 py-0.5 border-r border-black">
                    <EditableField
                      value={editableData.dailyFreeKm}
                      onChange={(val) => handleFieldChange('dailyFreeKm', val)}
                      style={{ width: '80px', display: 'inline-block' }}
                    />
                    <span style={{ marginLeft: '80px' }}>Km for the entire Trip</span>
                    <span style={{ marginLeft: '80px' }}>{(parseFloat(editableData.dailyFreeKm) * parseFloat(editableData.totalDays)).toFixed(0)}</span>
                  </td>
                </tr>
              </tbody>
            </table>
          </div>

          {/* Charges Table */}
          <div className="border-r border-black">
            <div className="font-bold text-center" style={{ borderTop: '3px double #000', borderBottom: '3px double #000', padding: '0px' }}>**CHARGES (AUD)**</div>
            <table className="w-full border-collapse" style={{ fontSize: '13px', tableLayout: 'fixed' }}>
              <tbody>
                <tr className="border-t border-b border-black">
                  <td className="px-1 py-0.5 border-r border-black" style={{ whiteSpace: 'nowrap', width: '255px' }}>1-Day Rate $:</td>
                  <td className={`px-2 ${days === 1 ? 'font-bold' : ''}`} style={{ whiteSpace: 'nowrap', width: '120px' }}>
                    {days === 1 ? get1DayRate().toFixed(2) : '0.00'}
                  </td>
                  <td className="px-2" style={{ padding: '0px 2px', width: '80px' }}></td>
                  <td className="px-2" style={{ padding: '0px 2px', width: '80px' }}></td>
                  <td className="border-l border-black px-1 py-0.5" style={{ padding: '0px 2px', whiteSpace: 'nowrap', width: '25%' }}>Addl kms $:</td>
                  <td className="text-right px-1 py-0.5 font-bold" style={{ padding: '0px 2px', whiteSpace: 'nowrap', width: '20%' }}>.29 cent/kms</td>
                </tr>
                <tr className="border-b border-black">
                  <td className="px-1 py-0.5 border-r border-black" style={{ whiteSpace: 'nowrap' }}>2-Days Daily $:</td>
                  <td className={`px-2 ${days === 2 ? 'font-bold' : ''}`} style={{ whiteSpace: 'nowrap' }}>
                    {days === 2 ? get2DayRate().toFixed(2) : '0.00'}
                  </td>
                  <td className="px-2" style={{ padding: '0px 2px' }}></td>
                  <td className="px-2" style={{ padding: '0px 2px' }}></td>
                  <td className="border-l border-black px-1 py-0.5" style={{ padding: '0px 2px', whiteSpace: 'nowrap' }}>Fuel Service</td>
                  <td className="text-right px-1 py-0.5 font-bold" style={{ padding: '0px 2px', whiteSpace: 'nowrap' }}>$3.0/liter</td>
                </tr>
                <tr className="border-b border-black">
                  <td className="px-1 py-0.5 border-r border-black" style={{ whiteSpace: 'nowrap' }}>3-6 Days Daily $:</td>
                  <td className={`px-2 ${days >= 3 && days <= 6 ? 'font-bold' : ''}`} style={{ whiteSpace: 'nowrap' }}>
                    {days >= 3 && days <= 6 ? get3To6DayRate().toFixed(2) : '0.00'}
                  </td>
                  <td className="px-2" style={{ padding: '0px 2px' }}></td>
                  <td className="px-2" style={{ padding: '0px 2px' }}></td>
                  <td className="border-l border-black px-1 py-0.5" style={{ padding: '0px 2px', whiteSpace: 'nowrap' }}></td>
                  <td className="text-right px-1 py-0.5" style={{ padding: '0px 2px', whiteSpace: 'nowrap' }}>
                    
                  </td>
                </tr>
                <tr className="border-b border-black">
                  <td className="px-1 py-0.5 border-r border-black" style={{ whiteSpace: 'nowrap' }}>7+ Days Daily $:</td>
                  <td className={`px-2 ${days >= 7 ? 'font-bold' : ''}`} style={{ whiteSpace: 'nowrap' }}>
                    {days >= 7 ? get7PlusDayRate().toFixed(2) : '0.00'}
                  </td>
                  <td className="px-2" style={{ padding: '0px 2px' }}></td>
                  <td className="px-2" style={{ padding: '0px 2px' }}></td>
                  <td className="border-l border-black px-1 py-0.5" style={{ padding: '0px 2px', whiteSpace: 'nowrap' }}></td>
                  <td className="text-right px-1 py-0.5" style={{ padding: '0px 2px', whiteSpace: 'nowrap' }}>
                    
                  </td>
                </tr>
                <tr className="border-b border-black">
                  <td className="px-1 py-0.5 border-r border-black" style={{ whiteSpace: 'nowrap' }}>Damage Waiver $:</td>
                  <td className={`px-2 ${damageWaiverAmount > 0 ? 'font-bold' : ''}`} style={{ whiteSpace: 'nowrap' }}>
                    {damageWaiverAmount.toFixed(2)}
                  </td>
                  <td className="px-2" style={{ padding: '0px 2px' }}></td>
                  <td className="px-2" style={{ padding: '0px 2px' }}></td>
                  <td className="border-l border-black px-1 py-0.5" style={{ padding: '0px 2px' }}></td>
                  <td className="text-right px-1 py-0.5" style={{ padding: '0px 2px' }}></td>
                </tr>
                <tr className="border-b border-black">
                  <td className="px-1 py-0.5 border-r border-black" style={{ whiteSpace: 'nowrap' }}>Baby Seat $:</td>
                  <td className={`px-2 ${babySeatAmount > 0 ? 'font-bold' : ''}`} style={{ whiteSpace: 'nowrap' }}>
                    {babySeatAmount.toFixed(2)}
                  </td>
                  <td className="px-2" style={{ padding: '0px 2px' }}></td>
                  <td className="px-2" style={{ padding: '0px 2px' }}></td>
                  <td className="border-l border-black px-1 py-0.5" style={{ padding: '0px 2px' }}></td>
                  <td className="px-2" style={{ padding: '0px 2px' }}></td>
                </tr>
                <tr className="border-b border-black">
                  <td className="px-1 py-0.5 border-r border-black" style={{ whiteSpace: 'nowrap' }}>Extended Area of Use $:</td>
                  <td className={`px-2 ${extendedAreaAmount > 0 ? 'font-bold' : ''}`} style={{ whiteSpace: 'nowrap' }}>
                    {extendedAreaAmount.toFixed(2)}
                  </td>
                  <td className="px-2" style={{ padding: '0px 2px' }}></td>
                  <td className="px-2" style={{ padding: '0px 2px' }}></td>
                  <td className="px-2" style={{ padding: '0px 2px' }}></td>
                  <td className="px-2" style={{ padding: '0px 2px' }}></td>
                </tr>
                <tr className="border-b border-black">
                  <td className="px-1 py-0.5 border-r border-black" style={{ whiteSpace: 'nowrap' }}>Airport/Pickup-service $:</td>
                  <td className={`px-2 ${airportPickupAmount > 0 ? 'font-bold' : ''}`} style={{ whiteSpace: 'nowrap' }}>
                    {airportPickupAmount.toFixed(2)}
                  </td>
                  <td className="px-2" style={{ padding: '0px 2px' }}></td>
                  <td className="px-2" style={{ padding: '0px 2px' }}></td>
                  <td className="px-2" style={{ padding: '0px 2px' }}></td>
                  <td className="px-2" style={{ padding: '0px 2px' }}></td>
                </tr>
                <tr className="border-b border-black">
                  <td className="px-1 py-0.5 border-r border-black" style={{ whiteSpace: 'nowrap' }}>Airport/Dropoff-service $:</td>
                  <td className={`px-2 ${airportDropoffAmount > 0 ? 'font-bold' : ''}`} style={{ whiteSpace: 'nowrap' }}>
                    {airportDropoffAmount.toFixed(2)}
                  </td>
                  <td className="px-2" style={{ padding: '0px 2px' }}></td>
                  <td className="px-2" style={{ padding: '0px 2px' }}></td>
                  <td className="px-2" style={{ padding: '0px 2px' }}></td>
                  <td className="px-2" style={{ padding: '0px 2px' }}></td>
                </tr>
                <tr className="border-b border-black">
                  <td className="px-1 py-0.5 border-r border-black">Addl kms/Unlimited Kms Charge $</td>
                  <td className="px-2" style={{ whiteSpace: 'nowrap' }}>
                    <EditableField
                      value={editableData.additionalKmsCharge}
                      onChange={(val) => handleFieldChange('additionalKmsCharge', val)}
                    />
                  </td>
                  <td className="px-2" colSpan={4} style={{ padding: '0px 2px' }}>
                    <EditableField
                      value={editableData.additionalKmsDescription}
                      onChange={(val) => handleFieldChange('additionalKmsDescription', val)}
                    />
                  </td>
                </tr>
                <tr className="border-b border-black">
                  <td className="px-1 py-0.5 border-r border-black" style={{ whiteSpace: 'nowrap' }}>Hire Duration:</td>
                  <td className="px-2" style={{ whiteSpace: 'nowrap' }}>{days}.00</td>
                  <td className="px-2" style={{ padding: '0px 2px' }}></td>
                  <td className="px-2" style={{ padding: '0px 2px' }}></td>
                  <td className="px-2" style={{ padding: '0px 2px' }}></td>
                  <td className="px-2" style={{ padding: '0px 2px' }}></td>
                </tr>
                <tr className="border-b border-black">
                  <td className="px-1 py-0.5 border-r border-black" style={{ whiteSpace: 'nowrap' }}>Security Deposit $:</td>
                  <td className="px-2" style={{ whiteSpace: 'nowrap' }}>
                    <EditableField
                      value={editableData.securityDeposit}
                      onChange={(val) => handleFieldChange('securityDeposit', val)}
                    />
                  </td>
                  <td className="px-2" colSpan={4} style={{ padding: '0px 2px' }}>Deposit will be refunded upon vehicle return</td>
                </tr>
                <tr className="border-b border-black">
                  <td className="px-1 py-0.5 border-r border-black font-bold" style={{ whiteSpace: 'nowrap' }}>Total GST $</td>
                  <td className="px-2 font-bold" style={{ whiteSpace: 'nowrap' }}>
                    <EditableField
                      value={editableData.totalGST}
                      onChange={(val) => handleFieldChange('totalGST', val)}
                      className="font-bold"
                    />
                  </td>
                  <td className="px-2" style={{ padding: '0px 2px' }}></td>
                  <td className="px-2" style={{ padding: '0px 2px' }}></td>
                  <td className="px-2" style={{ padding: '0px 2px' }}></td>
                  <td className="px-2" style={{ padding: '0px 2px' }}></td>
                </tr>
                <tr className="border-b-2 border-black">
                  <td className="px-1 py-0.5 border-r border-black font-bold" style={{ whiteSpace: 'nowrap' }}>Special Discount $:</td>
                  <td className="px-2 font-bold" style={{ whiteSpace: 'nowrap' }}>
                    -<EditableField
                      value={editableData.specialDiscount}
                      onChange={(val) => handleFieldChange('specialDiscount', val)}
                      className="font-bold"
                      style={{ width: '80px', display: 'inline-block' }}
                    />
                  </td>
                  <td className="px-2" style={{ padding: '0px 2px' }}></td>
                  <td className="px-2" style={{ padding: '0px 2px' }}></td>
                  <td className="px-2" style={{ padding: '0px 2px' }}></td>
                  <td className="px-2" style={{ padding: '0px 2px' }}></td>
                </tr>
                <tr className="border-b-2 border-black bg-gray-100">
                  <td className="px-1 py-0.5 border-r border-black font-bold text-lg" style={{ whiteSpace: 'nowrap' }}>Total amount+GST $</td>
                  <td className="px-2 font-bold text-lg" style={{ whiteSpace: 'nowrap' }}>
                    <EditableField
                      value={editableData.totalAmount}
                      onChange={(val) => handleFieldChange('totalAmount', val)}
                      className="font-bold text-lg"
                    />
                  </td>
                  <td className="px-2" style={{ padding: '0px 2px' }}></td>
                  <td className="px-2" style={{ padding: '0px 2px' }}></td>
                  <td className="px-2" style={{ padding: '0px 2px' }}></td>
                  <td className="px-2" style={{ padding: '0px 2px' }}></td>
                </tr>
                <tr>
                  <td className="px-2" colSpan={6} style={{ padding: '0px 2px' }}></td>
                </tr>

                <tr className="border-t border-b border-black">
                  <td className="border-r border-black px-2 font-bold" style={{ padding: '0px 2px' }}>Special Comments & Conditions</td>
                  <td className="px-2" colSpan={5} style={{ padding: '0px 2px' }}>
                    <EditableField
                      value={editableData.specialComments}
                      onChange={(val) => handleFieldChange('specialComments', val)}
                      multiline
                      rows={2}
                      className="w-full"
                      style={{ width: '100%' }}
                    />
                  </td>
                </tr>
              </tbody>
            </table>
          </div>

          {/* Agreement Section */}
          <div style={{ marginTop: '15px' }}>
            <div className="font-bold text-center" style={{ borderTop: '3px double #000', borderBottom: '3px double #000', padding: '0', margin:'5px' }}>Agreement to rent Vehicle</div>
            <div style={{ fontSize: '11px', lineHeight: '1.0' }}>
              I verify the details provided above, agree to the terms and conditions, and authorize the charges to my credit card as indicated on the receipt
            </div>
          </div>

          {/* Signature */}
          <div style={{ marginTop: '10px', paddingTop: '5px' }}>
            <div className="flex justify-between items-end">
              <div>
                <div style={{ marginBottom: '26px' }}>Customer Signature X</div>
                <div className="border-b-2 border-black" style={{ width: '320px' }}></div>
              </div>
              {printedAt && (
                <div style={{ fontSize: '10px', textAlign: 'right', alignSelf: 'flex-end', marginBottom: '2px' }}>
                  Printed: {printedAt}
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Print Styles */}
      <style>{`
        @media print {
          @page {
            size: A4;
            margin: 5mm;
          }

          * {
            -webkit-print-color-adjust: exact !important;
            print-color-adjust: exact !important;
            color-adjust: exact !important;
          }

          body * {
            visibility: hidden;
          }

          .print\\:hidden {
            display: none !important;
          }

          #invoice-content,
          #invoice-content * {
            visibility: visible;
          }

          #invoice-content {
            position: absolute;
            left: 0;
            top: 0;
            width: 100%;
            max-width: 210mm;
            padding: 5mm;
            margin: 0 auto;
            font-family: 'Courier New', monospace !important;
            font-size: 12px !important;
            line-height: 1.0 !important;
            page-break-inside: avoid;
            box-sizing: border-box;
          }

          /* Ensure signature area is visible */
          #invoice-content > div:last-child {
            page-break-inside: avoid;
            margin-bottom: 10mm;
          }

          #invoice-content table {
            border-collapse: collapse !important;
            table-layout: fixed !important;
          }

          #invoice-content .px-2 {
            padding-left: 0.5rem !important;
            padding-right: 0.5rem !important;
          }

          #invoice-content .border-r {
            border-right: 1px solid #000 !important;
          }
          #invoice-content .border-l {
            border-left: 1px solid #000 !important;
          }
          #invoice-content .border-t {
            border-top: 1px solid #000 !important;
          }
          #invoice-content .border-b {
            border-bottom: 1px solid #000 !important;
          }
          #invoice-content .border-b-2 {
            border-bottom: 2px solid #000 !important;
          }

          #invoice-content .bg-gray-100 {
            background-color: #f3f4f6 !important;
          }
          #invoice-content .font-bold {
            font-weight: bold !important;
          }
          #invoice-content .text-lg {
            font-size: 1.125rem !important;
          }

          .print\\:p-0 { padding: 0 !important; }
          .print\\:bg-white { background: white !important; }
          .print\\:shadow-none { box-shadow: none !important; }
          .print\\:rounded-none { border-radius: 0 !important; }
          .print\\:max-h-none { max-height: none !important; }
        }
      `}</style>
    </div>
  );
};

export default BookingInvoice;
